import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { baseurladdress } from "../baseurl";
import { Button, Form, InputGroup, FormControl, Modal } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from "@tanstack/react-table";

function Arealist() {
    const [activeTab, setActiveTab] = useState("regions");
    const [regions, setRegions] = useState([]);
    const [areas, setAreas] = useState([]);

    const [managers, setManagers] = useState([]);
    const [selectedManager, setSelectedManager] = useState("");

    const [showManagerModal, setShowManagerModal] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [assignType, setAssignType] = useState("");

    const [loading, setLoading] = useState(false);
    const [loadingManagers, setLoadingManagers] = useState(false);

    // TanStack Table states for Regions
    const [regionsGlobalFilter, setRegionsGlobalFilter] = useState("");
    const [regionsSorting, setRegionsSorting] = useState([]);

    // TanStack Table states for Areas
    const [areasGlobalFilter, setAreasGlobalFilter] = useState("");
    const [areasSorting, setAreasSorting] = useState([]);

    /* ---------------- Fetch Regions ---------------- */
    const fetchRegions = async () => {
        try {
            setLoading(true);
            const res = await axios.get(baseurladdress + "region/getregions", {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });

            if (res.data.status === "Success") {
                setRegions(res.data.regions);
            }
        } catch {
            Swal.fire("Error", "Error loading regions", "error");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- Fetch Areas ---------------- */
    const fetchAreas = async () => {
        try {
            setLoading(true);
            const res = await axios.get(baseurladdress + "area/getareas", {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });

            if (res.data.status === "Success") {
                setAreas(res.data.areas);
            }
        } catch {
            Swal.fire("Error", "Error loading areas", "error");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- Fetch Managers ---------------- */
    const fetchManagers = async (type) => {
        try {
            setLoadingManagers(true);
            setManagers([]);

            const endpoint =
                type === "region"
                    ? "region/regional-managers"
                    : "area/area-managers";

            const res = await axios.get(baseurladdress + endpoint, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });

            if (res.data.status === "Success") {
                setManagers(res.data.managers);
            }
        } catch {
            Swal.fire("Error", "Error loading managers", "error");
        } finally {
            setLoadingManagers(false);
        }
    };

    /* ---------------- Assign Manager ---------------- */
    const assignManager = async () => {
        if (!selectedManager) return;

        try {
            setLoading(true);

            const payload =
                assignType === "region"
                    ? { region_id: selectedEntity.region_id, manager_id: selectedManager }
                    : { area_id: selectedEntity.area_id, manager_id: selectedManager };

            const url =
                assignType === "region"
                    ? "region/assign-manager"
                    : "area/assign-manager";

            const res = await axios.post(baseurladdress + url, payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });

            if (res.data.status === "Success") {
                Swal.fire({
                    toast: true,
                    position: "top-end",
                    icon: "success",
                    title: "Manager assigned",
                    showConfirmButton: false,
                    timer: 1500,
                });

                setShowManagerModal(false);
                setSelectedManager("");
                assignType === "region" ? fetchRegions() : fetchAreas();
            }
        } catch {
            Swal.fire("Error", "Failed to assign manager", "error");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- Delete Region ---------------- */
    const deleteRegion = async (region_id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This region will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete",
        });

        if (!result.isConfirmed) return;

        await axios.post(
            baseurladdress + `region/deleteregion/${region_id}`,
            {},
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );

        fetchRegions();
    };

    const deleteArea = async (area_id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This area will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes, delete",
        });

        if (!result.isConfirmed) return;

        try {
            const res = await axios.post(
                baseurladdress + `area/deletearea/${area_id}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            if (
                res.data.status === "Success" ||
                res.data.status === "success" ||
                res.data.message === "Area deleted successfully"
            ) {
                Swal.fire({
                    toast: true,
                    position: "top-end",
                    icon: "success",
                    title: "Area deleted successfully",
                    showConfirmButton: false,
                    timer: 1500,
                });
                fetchAreas();
            } else {
                Swal.fire("Error", res.data.message || "Failed to delete area", "error");
            }
        } catch (err) {
            console.error("Delete error:", err);
            Swal.fire("Error", "Failed to delete area", "error");
        }
    };

    useEffect(() => {
        fetchRegions();
        fetchAreas();
    }, []);

    // Regions Table Columns
    const regionsColumns = useMemo(
        () => [
            {
                accessorKey: "index",
                header: "Sl.No",
                size: 60,
                cell: ({ row }) => row.index + 1,
            },
            {
                accessorKey: "region_name",
                header: "Region",
                size: 200,
            },
            {
                accessorKey: "region_manager_name",
                header: "Manager",
                size: 200,
                cell: ({ row }) => (
                    <span className={`badge ${row.original.region_manager_name ? "bg-success" : "bg-secondary"}`}>
                        {row.original.region_manager_name || "Not Assigned"}
                    </span>
                ),
            },
            {
                accessorKey: "actions",
                header: "Actions",
                size: 120,
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="d-flex gap-2 align-items-center">
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary text-decoration-none"
                            onClick={(e) => {
                                setSelectedEntity(row.original);
                                setAssignType("region");
                                setShowManagerModal(true);
                                setSelectedManager("");
                                fetchManagers("region");
                                e.currentTarget.blur();
                            }}
                        >
                            {row.original.region_manager_name ? "Reassign" : "Assign"}
                        </Button>
                        <Icon.Trash
                            className="text-danger"
                            size={18}
                            onClick={() => deleteRegion(row.original.region_id)}
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                ),
            },
        ],
        []
    );

    // Areas Table Columns
    const areasColumns = useMemo(
        () => [
            {
                accessorKey: "index",
                header: "Sl.No",
                size: 60,
                cell: ({ row }) => row.index + 1,
            },
            {
                accessorKey: "area_name",
                header: "Area",
                size: 180,
            },
            {
                accessorKey: "region_name",
                header: "Region",
                size: 150,
            },
            {
                accessorKey: "area_manager_name",
                header: "Manager",
                size: 180,
                cell: ({ row }) => (
                    <span className={`badge ${row.original.area_manager_name ? "bg-success" : "bg-secondary"}`}>
                        {row.original.area_manager_name || "Not Assigned"}
                    </span>
                ),
            },
            {
                accessorKey: "actions",
                header: "Actions",
                size: 120,
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="d-flex gap-2 align-items-center">
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary text-decoration-none"
                            onClick={(e) => {
                                setSelectedEntity(row.original);
                                setAssignType("area");
                                setShowManagerModal(true);
                                setSelectedManager("");
                                fetchManagers("area");
                                e.currentTarget.blur();
                            }}
                        >
                            {row.original.area_manager_name ? "Reassign" : "Assign"}
                        </Button>
                        <Icon.Trash
                            className="text-danger"
                            size={18}
                            onClick={() => deleteArea(row.original.area_id)}
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                ),
            },
        ],
        []
    );

    // Initialize Regions Table
    const regionsTable = useReactTable({
        data: regions,
        columns: regionsColumns,
        state: {
            globalFilter: regionsGlobalFilter,
            sorting: regionsSorting,
        },
        onGlobalFilterChange: setRegionsGlobalFilter,
        onSortingChange: setRegionsSorting,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    // Initialize Areas Table
    const areasTable = useReactTable({
        data: areas,
        columns: areasColumns,
        state: {
            globalFilter: areasGlobalFilter,
            sorting: areasSorting,
        },
        onGlobalFilterChange: setAreasGlobalFilter,
        onSortingChange: setAreasSorting,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    return (
        <div className="content-wrap">
            <div className="main">
                <div className="container-fluid">
                    <section id="main-content">
                        <div className="card">
                            <div
                                className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-3"
                            >
                                <h6 className="mb-0">REGIONS & AREAS</h6>

                                {/* Tabs moved to right */}
                                <div className="btn-group btn-group-sm">
                                    <button
                                        className={`btn ${activeTab === "regions" ? "btn-light" : "btn-outline-light"}`}
                                        onClick={(e) => {
                                            const target = e.currentTarget;
                                            setActiveTab("regions");
                                            setTimeout(() => target.blur(), 0);
                                        }}
                                    >
                                        Regions
                                    </button>
                                    <button
                                        className={`btn ${activeTab === "areas" ? "btn-light" : "btn-outline-light"}`}
                                        onClick={(e) => {
                                            const target = e.currentTarget;
                                            setActiveTab("areas");
                                            setTimeout(() => target.blur(), 0);
                                        }}
                                    >
                                        Areas
                                    </button>
                                </div>
                            </div>


                            <div className="card-body">

                                {/* ---------------- REGIONS TABLE ---------------- */}
                                {activeTab === "regions" && (
                                    <>
                                        {/* Search and Controls */}
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="text-muted small">Show</span>
                                                <Form.Select
                                                    size="sm"
                                                    style={{ width: "70px" }}
                                                    value={regionsTable.getState().pagination.pageSize}
                                                    onChange={(e) => {
                                                        regionsTable.setPageSize(Number(e.target.value));
                                                    }}
                                                >
                                                    {[5, 10, 20, 50].map((pageSize) => (
                                                        <option key={pageSize} value={pageSize}>
                                                            {pageSize}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <span className="text-muted small">entries</span>
                                            </div>

                                            <InputGroup style={{ width: "250px" }}>
                                                <InputGroup.Text>
                                                    <Icon.Search size={14} />
                                                </InputGroup.Text>
                                                <FormControl
                                                    size="sm"
                                                    placeholder="Search..."
                                                    value={regionsGlobalFilter ?? ""}
                                                    onChange={(e) => setRegionsGlobalFilter(e.target.value)}
                                                />
                                            </InputGroup>
                                        </div>

                                        {/* Table */}
                                        <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
                                            <table className="table table-hover table-sm align-middle" style={{ borderCollapse: 'collapse' }}
                                            >
                                                <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                                                    {regionsTable.getHeaderGroups().map((headerGroup) => (
                                                        <tr key={headerGroup.id}>
                                                            {headerGroup.headers.map((header) => (
                                                                <th
                                                                    key={header.id}
                                                                    style={{ width: header.getSize() }}
                                                                    className="text-nowrap"
                                                                >
                                                                    {header.isPlaceholder ? null : (
                                                                        <div
                                                                            {...{
                                                                                className: header.column.getCanSort()
                                                                                    ? "cursor-pointer select-none d-flex align-items-center"
                                                                                    : "",
                                                                                onClick: header.column.getToggleSortingHandler(),
                                                                            }}
                                                                        >
                                                                            {flexRender(
                                                                                header.column.columnDef.header,
                                                                                header.getContext()
                                                                            )}
                                                                            {header.column.getCanSort() && (
                                                                                <span className="ms-1">
                                                                                    {{
                                                                                        asc: <Icon.ChevronUp size={12} />,
                                                                                        desc: <Icon.ChevronDown size={12} />,
                                                                                    }[header.column.getIsSorted()] ?? (
                                                                                            <Icon.ChevronExpand size={12} className="text-muted" />
                                                                                        )}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </thead>
                                                <tbody>
                                                    {regionsTable.getRowModel().rows.length > 0 ? (
                                                        regionsTable.getRowModel().rows.map((row) => (
                                                            <tr key={row.id}>
                                                                {row.getVisibleCells().map((cell) => (
                                                                    <td key={cell.id} >
                                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={regionsColumns.length} className="text-center text-muted py-4">
                                                                No regions found
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div className="text-muted small">
                                                Showing {regionsTable.getState().pagination.pageIndex * regionsTable.getState().pagination.pageSize + 1} to{" "}
                                                {Math.min(
                                                    (regionsTable.getState().pagination.pageIndex + 1) * regionsTable.getState().pagination.pageSize,
                                                    regionsTable.getFilteredRowModel().rows.length
                                                )}{" "}
                                                of {regionsTable.getFilteredRowModel().rows.length} entries
                                            </div>

                                            <div className="btn-group btn-group-sm">
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => regionsTable.setPageIndex(0)}
                                                    disabled={!regionsTable.getCanPreviousPage()}
                                                >
                                                    <Icon.ChevronDoubleLeft size={14} />
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => regionsTable.previousPage()}
                                                    disabled={!regionsTable.getCanPreviousPage()}
                                                >
                                                    <Icon.ChevronLeft size={14} />
                                                </Button>
                                                <Button variant="outline-secondary" disabled>
                                                    Page {regionsTable.getState().pagination.pageIndex + 1} of {regionsTable.getPageCount()}
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => regionsTable.nextPage()}
                                                    disabled={!regionsTable.getCanNextPage()}
                                                >
                                                    <Icon.ChevronRight size={14} />
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => regionsTable.setPageIndex(regionsTable.getPageCount() - 1)}
                                                    disabled={!regionsTable.getCanNextPage()}
                                                >
                                                    <Icon.ChevronDoubleRight size={14} />
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* ---------------- AREAS TABLE ---------------- */}
                                {activeTab === "areas" && (
                                    <>
                                        {/* Search and Controls */}
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="text-muted small">Show</span>
                                                <Form.Select
                                                    size="sm"
                                                    style={{ width: "70px" }}
                                                    value={areasTable.getState().pagination.pageSize}
                                                    onChange={(e) => {
                                                        areasTable.setPageSize(Number(e.target.value));
                                                    }}
                                                >
                                                    {[5, 10, 20, 50].map((pageSize) => (
                                                        <option key={pageSize} value={pageSize}>
                                                            {pageSize}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <span className="text-muted small">entries</span>
                                            </div>

                                            <InputGroup style={{ width: "250px" }}>
                                                <InputGroup.Text>
                                                    <Icon.Search size={14} />
                                                </InputGroup.Text>
                                                <FormControl
                                                    size="sm"
                                                    placeholder="Search..."
                                                    value={areasGlobalFilter ?? ""}
                                                    onChange={(e) => setAreasGlobalFilter(e.target.value)}
                                                />
                                            </InputGroup>
                                        </div>

                                        {/* Table */}
                                        <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
                                            <table className="table table-hover table-sm align-middle" style={{ borderCollapse: 'collapse' }}>
                                                <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                                                    {areasTable.getHeaderGroups().map((headerGroup) => (
                                                        <tr key={headerGroup.id}>
                                                            {headerGroup.headers.map((header) => (
                                                                <th
                                                                    key={header.id}
                                                                    style={{ width: header.getSize() }}
                                                                    className="text-nowrap"
                                                                >
                                                                    {header.isPlaceholder ? null : (
                                                                        <div
                                                                            {...{
                                                                                className: header.column.getCanSort()
                                                                                    ? "cursor-pointer select-none d-flex align-items-center"
                                                                                    : "",
                                                                                onClick: header.column.getToggleSortingHandler(),
                                                                            }}
                                                                        >
                                                                            {flexRender(
                                                                                header.column.columnDef.header,
                                                                                header.getContext()
                                                                            )}
                                                                            {header.column.getCanSort() && (
                                                                                <span className="ms-1">
                                                                                    {{
                                                                                        asc: <Icon.ChevronUp size={12} />,
                                                                                        desc: <Icon.ChevronDown size={12} />,
                                                                                    }[header.column.getIsSorted()] ?? (
                                                                                            <Icon.ChevronExpand size={12} className="text-muted" />
                                                                                        )}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </thead>
                                                <tbody>
                                                    {areasTable.getRowModel().rows.length > 0 ? (
                                                        areasTable.getRowModel().rows.map((row) => (
                                                            <tr key={row.id}>
                                                                {row.getVisibleCells().map((cell) => (
                                                                    <td key={cell.id}>
                                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={areasColumns.length} className="text-center text-muted py-4">
                                                                No areas found
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div className="text-muted small">
                                                Showing {areasTable.getState().pagination.pageIndex * areasTable.getState().pagination.pageSize + 1} to{" "}
                                                {Math.min(
                                                    (areasTable.getState().pagination.pageIndex + 1) * areasTable.getState().pagination.pageSize,
                                                    areasTable.getFilteredRowModel().rows.length
                                                )}{" "}
                                                of {areasTable.getFilteredRowModel().rows.length} entries
                                            </div>

                                            <div className="btn-group btn-group-sm">
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => areasTable.setPageIndex(0)}
                                                    disabled={!areasTable.getCanPreviousPage()}
                                                >
                                                    <Icon.ChevronDoubleLeft size={14} />
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => areasTable.previousPage()}
                                                    disabled={!areasTable.getCanPreviousPage()}
                                                >
                                                    <Icon.ChevronLeft size={14} />
                                                </Button>
                                                <Button variant="outline-secondary" disabled>
                                                    Page {areasTable.getState().pagination.pageIndex + 1} of {areasTable.getPageCount()}
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => areasTable.nextPage()}
                                                    disabled={!areasTable.getCanNextPage()}
                                                >
                                                    <Icon.ChevronRight size={14} />
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => areasTable.setPageIndex(areasTable.getPageCount() - 1)}
                                                    disabled={!areasTable.getCanNextPage()}
                                                >
                                                    <Icon.ChevronDoubleRight size={14} />
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* ---------------- MODAL ---------------- */}
            <Modal 
                className="modal-size md modal-top-center"
                show={showManagerModal} 
                onHide={() => setShowManagerModal(false)} 
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header>
                    <h6>ASSIGN MANAGER</h6>
                    <span
                        aria-hidden="true"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowManagerModal(false)}
                    >
                        ×
                    </span>
                </Modal.Header>

                <Modal.Body className="css">
                    {loadingManagers ? (
                        <div className="text-center">Loading...</div>
                    ) : (
                        <form>
                            <div className="row">
                                <div className="col-md-12 mt-2">
                                    <h6>Select Manager</h6>
                                    <select
                                        className="form-control"
                                        value={selectedManager}
                                        onChange={(e) => setSelectedManager(e.target.value)}
                                    >
                                        <option value="">-- Select Manager --</option>
                                        {managers.map((m) => (
                                            <option key={m.user_id} value={m.user_id}>
                                                {m.user_staffname}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </form>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <button 
                        className="btn btn-primary" 
                        onClick={assignManager} 
                        disabled={!selectedManager}
                    >
                        Assign
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default Arealist;